/*============================================================================
  Free Gift Size Picker
  ----------------------------------------------------------------------------
  Shows a picker (e.g. Size) when a qualifying "Buy X Get Y" trigger
  product is in the cart, lets the customer choose the non-color option(s)
  on their free gift, adds the matching variant, and keeps things tidy
  (auto-removes the gift if the trigger product is later removed).

  Color is intentionally NOT shown as a picker. If the gift product has a
  "Color" option, this script automatically resolves a valid/available
  color behind the scenes so the variant lookup still works — the customer
  just won't see color buttons.

  Works with the gift product's options exactly as they're set up in
  Shopify admin — if it only has "Size", you'll just see size buttons; if it
  has "Color" and "Size", you'll only see the Size buttons (Color is
  auto-resolved).

  SETUP — you MUST edit the two values below before this works:
    - triggerHandles : the product handle(s) of the item(s) that unlock
                        the free gift (e.g. the Air Freshener Combo Pack)
    - giftHandle     : the product handle of the free gift item
                        (e.g. the T-Shirt)

  Find a product's handle in its storefront URL:
    https://yourstore.com/products/THIS-PART-IS-THE-HANDLE

  INSTALL:
    1. Create this file as  assets/free-gift.js  in your theme.
    2. Add this single line near the bottom of
       snippets/ajax-cart-template.liquid (after the existing <script> tags):
         <script src="{{ 'free-gift.js' | asset_url }}" defer="defer"></script>
       That snippet loads sitewide, so this covers both the drawer and the
       cart page with one include.
============================================================================*/
(function () {
  'use strict';

  var CONFIG = {
    triggerHandles: ['hni-air-fragrance-combo-pack', 'hni-air-freshener-combo-with-floral-and-fresh-notes'],       // <-- EDIT ME
    giftHandle: 'plain-white-tshirt-100-cotton', // <-- EDIT ME
    giftsPerTrigger: 1 // how many free gifts to allow (kept simple: max 1 total for now)
  };

  var giftProductCache = null;

  function fetchGiftProduct(cb) {
    if (giftProductCache) { cb(giftProductCache); return; }
    fetch('/products/' + CONFIG.giftHandle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        giftProductCache = data;
        cb(data);
      })
      .catch(function (err) {
        console.error('Free gift: could not load gift product', err);
      });
  }

  function getCart(cb) {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function (err) {
        console.error('Free gift: could not load cart', err);
      });
  }

  function cartHasTrigger(cart) {
    return cart.items.some(function (item) {
      return CONFIG.triggerHandles.indexOf(item.handle) !== -1;
    });
  }

  function findGiftLine(cart) {
    return cart.items.find(function (item) {
      return item.handle === CONFIG.giftHandle &&
        item.properties && item.properties._free_gift === 'true';
    });
  }

  function addGift(variantId, cb) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { '_free_gift': 'true' }
      })
    })
      .then(function (r) { return r.json(); })
      .then(function () { cb(); })
      .catch(function (err) { console.error('Free gift: add failed', err); });
  }

  function removeGiftLine(item, cb) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { cb(); })
      .catch(function (err) { console.error('Free gift: remove failed', err); });
  }

  /* ---- Option helpers -----------------------------------------------
     Normalizes product.options (Shopify's /products/handle.js sometimes
     returns an array of plain strings, sometimes an array of
     {name, values} objects) into: [{ name, values: [...] }, ...],
     where values are ordered as they first appear on the product.
  --------------------------------------------------------------------- */
  function deriveValuesFromVariants(product, optionIndex) {
    var seen = {};
    var values = [];
    product.variants.forEach(function (variant) {
      var val = variant['option' + (optionIndex + 1)];
      if (val && !seen[val]) {
        seen[val] = true;
        values.push(val);
      }
    });
    return values;
  }

  function getOptionMeta(product) {
    return product.options.map(function (opt, idx) {
      if (typeof opt === 'string') {
        return { name: opt, values: deriveValuesFromVariants(product, idx) };
      }
      return {
        name: opt.name,
        values: (opt.values && opt.values.length) ? opt.values : deriveValuesFromVariants(product, idx)
      };
    });
  }

  function isColorOption(name) {
    return /colou?r/i.test(name);
  }

  function findMatchingVariant(product, selections, optionCount) {
    return product.variants.filter(function (variant) {
      for (var idx = 0; idx < optionCount; idx++) {
        if (selections[idx] !== variant['option' + (idx + 1)]) return false;
      }
      return true;
    })[0];
  }

  // Is `value` (at option position optionIndex) selectable given what's
  // already picked for the other options? Used to grey out combos that
  // don't exist or are sold out (e.g. a size not offered in a given color).
  function isValueAvailable(product, optionIndex, value, selections) {
    return product.variants.some(function (variant) {
      if (variant['option' + (optionIndex + 1)] !== value) return false;
      if (!variant.available) return false;
      for (var otherIdx in selections) {
        if (Number(otherIdx) === optionIndex) continue;
        var chosen = selections[otherIdx];
        if (chosen && variant['option' + (Number(otherIdx) + 1)] !== chosen) return false;
      }
      return true;
    });
  }

  // Auto-resolve a value for any option we don't show a picker for
  // (currently: Color). Picks the first value that's still consistent
  // with whatever the customer has already chosen elsewhere, preferring
  // one that leads to an available variant.
  function autoResolveHiddenOptions(product, meta, selections) {
    meta.forEach(function (opt, idx) {
      if (!isColorOption(opt.name)) return;
      var current = selections[idx];
      var stillValid = current && isValueAvailable(product, idx, current, selections);
      if (stillValid) return;
      var fallback = opt.values.filter(function (value) {
        return isValueAvailable(product, idx, value, selections);
      })[0];
      selections[idx] = fallback || opt.values[0];
    });
  }

  function buildPickerHtml(product) {
    var meta = getOptionMeta(product);

    var html = '<div class="free-gift-picker" id="FreeGiftPicker">';
    html += '<p class="free-gift-picker__title">\uD83C\uDF81 Your free gift is waiting \u2014 pick your options:</p>';

    meta.forEach(function (opt, idx) {
      if (isColorOption(opt.name)) return; // color is auto-resolved, not shown
      html += '<div class="free-gift-picker__option">';
      html += '<p class="free-gift-picker__option-label">' + opt.name + '</p>';
      html += '<div class="free-gift-picker__values" data-option-index="' + idx + '">';
      opt.values.forEach(function (value) {
        var available = isValueAvailable(product, idx, value, {});
        html += '<button type="button" class="free-gift-picker__value' +
          (available ? '' : ' is-soldout') + '" ' +
          'data-option-index="' + idx + '" data-value="' + value + '"' +
          (available ? '' : ' disabled') + '>' +
          value +
          '</button>';
      });
      html += '</div></div>';
    });

    html += '</div>';
    return html;
  }

  function buildAddedHtml(item) {
    var details = item.variant_title ? ' \u2014 ' + item.variant_title : '';
    return '<div class="free-gift-picker free-gift-picker--added" id="FreeGiftPicker">' +
      '<p>\uD83C\uDF81 Free gift added' + details +
      ' <button type="button" class="free-gift-picker__change" id="FreeGiftChange">Change</button></p>' +
      '</div>';
  }

  function refreshEverything() {
    if (document.querySelector('.cart_table')) {
      // Cart page has no client-side item renderer we can safely hook into,
      // so a reload is the reliable way to reflect the updated cart.
      window.location.reload();
      return;
    }
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    }
  }

  function bindPickerEvents(picker, product) {
    var meta = getOptionMeta(product);
    var visibleCount = meta.filter(function (opt) { return !isColorOption(opt.name); }).length;
    var selections = {}; // optionIndex -> chosen value

    // Resolve any hidden (color) options up front so a matching variant
    // can be found as soon as the visible options are picked.
    autoResolveHiddenOptions(product, meta, selections);

    function updateAvailability() {
      meta.forEach(function (opt, idx) {
        if (isColorOption(opt.name)) return;
        var group = picker.querySelector('.free-gift-picker__values[data-option-index="' + idx + '"]');
        if (!group) return;
        group.querySelectorAll('.free-gift-picker__value').forEach(function (btn) {
          var value = btn.getAttribute('data-value');
          var available = isValueAvailable(product, idx, value, selections);
          btn.disabled = !available;
          btn.classList.toggle('is-soldout', !available);
        });
      });
    }

    function tryAddIfComplete() {
      var chosenVisible = Object.keys(selections).filter(function (idx) {
        return !isColorOption(meta[idx].name);
      }).length;
      if (chosenVisible !== visibleCount) return;

      autoResolveHiddenOptions(product, meta, selections);

      var variant = findMatchingVariant(product, selections, meta.length);
      if (!variant || !variant.available) return;
      picker.querySelectorAll('.free-gift-picker__value').forEach(function (b) { b.disabled = true; });
      addGift(variant.id, refreshEverything);
    }

    meta.forEach(function (opt, idx) {
      if (isColorOption(opt.name)) return;
      var group = picker.querySelector('.free-gift-picker__values[data-option-index="' + idx + '"]');
      if (!group) return;
      group.querySelectorAll('.free-gift-picker__value').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          selections[idx] = btn.getAttribute('data-value');
          group.querySelectorAll('.free-gift-picker__value').forEach(function (b) {
            b.classList.toggle('is-selected', b === btn);
          });
          autoResolveHiddenOptions(product, meta, selections);
          updateAvailability();
          tryAddIfComplete();
        });
      });
    });
  }

  function renderPickerInto(container, cart) {
    if (!container) return;

    var existing = document.getElementById('FreeGiftPicker');
    if (existing) existing.parentNode.removeChild(existing);

    var hasTrigger = cartHasTrigger(cart);
    var giftItem = findGiftLine(cart);

    if (!hasTrigger) {
      // Qualifying product isn't in the cart (any more) — don't leave a
      // paid gift item behind.
      if (giftItem) {
        removeGiftLine(giftItem, refreshEverything);
      }
      return;
    }

    if (giftItem) {
      container.insertAdjacentHTML('afterbegin', buildAddedHtml(giftItem));
      var changeBtn = document.getElementById('FreeGiftChange');
      if (changeBtn) {
        changeBtn.addEventListener('click', function () {
          removeGiftLine(giftItem, refreshEverything);
        });
      }
      return;
    }

    fetchGiftProduct(function (product) {
      container.insertAdjacentHTML('afterbegin', buildPickerHtml(product));
      var picker = document.getElementById('FreeGiftPicker');
      if (!picker) return;
      bindPickerEvents(picker, product);
    });
  }

  function checkAndRenderDrawer(cart) {
    renderPickerInto(document.getElementById('CartContainer'), cart);
  }

  function checkAndRenderPage(cart) {
    renderPickerInto(document.querySelector('.cart__items'), cart);
  }

  function injectStyles() {
    var css = ''
      + '.free-gift-picker{border:2px solid var(--DTSecondaryColor);padding:14px 16px;margin-bottom:16px;font-family:var(--DTFontTypo_Body,sans-serif);}'
      + '.free-gift-picker__title{margin:0 0 10px;font-weight:500;font-size:var(--DTFontSizeBase,13px);}'
      + '.free-gift-picker__option{margin-bottom:10px;}'
      + '.free-gift-picker__option:last-child{margin-bottom:0;}'
      + '.free-gift-picker__option-label{margin:0 0 6px;font-size:var(--DTFontSizeBase,13px);opacity:.75;}'
      + '.free-gift-picker__values{display:flex;flex-wrap:wrap;gap:8px;}'
      + '.free-gift-picker__value{padding:8px 16px;border:2px solid var(--DTColor_Body);background:transparent;cursor:pointer;font-family:var(--DTFontTypo_Body,sans-serif);font-size:var(--DTFontSizeBase,13px);}'
      + '.free-gift-picker__value:hover:not([disabled]){border-color:var(--DTSecondaryColor);background:var(--DTSecondaryColor);}'
      + '.free-gift-picker__value.is-selected{border-color:var(--DTSecondaryColor);background:var(--DTSecondaryColor);}'
      + '.free-gift-picker__value.is-soldout{opacity:.4;text-decoration:line-through;cursor:not-allowed;}'
      + '.free-gift-picker--added p{margin:0;font-weight:500;}'
      + '.free-gift-picker__change{margin-left:8px;background:none;border:none;text-decoration:underline;cursor:pointer;font-size:inherit;color:inherit;padding:0;}';
    var style = document.createElement('style');
    style.setAttribute('data-free-gift-styles', 'true');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();

    if (window.jQuery) {
      jQuery(document.body).on('ajaxCart.afterCartLoad', function (evt, cart) {
        checkAndRenderDrawer(cart);
      });
    }

    // Initial check on page load (covers cart page + drawer pre-populated
    // from a previous session).
    getCart(function (cart) {
      checkAndRenderDrawer(cart);
      checkAndRenderPage(cart);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();